import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Reveal, Overline } from "@/components/Reveal";
import { FLAVOURS, ASSETS } from "@/lib/constants";

const JAR = { piri_piri: ASSETS.beetrootPiri, tango_tomato: ASSETS.plainTango, palak: ASSETS.palakPiri, beetroot: ASSETS.beetrootPiri };
const PARTICLES = { piri_piri: ["🌶", "🔥", "🌶"], tango_tomato: ["🍅", "🧄", "🍅"], palak: ["🥬", "🌿", "🥬"], beetroot: ["🍠", "💜", "🍠"] };

export default function FlavourExperience() {
  const [active, setActive] = useState(null);
  return (
    <section id="flavours" className="relative bg-forest-deep py-24 lg:py-32" data-testid="flavour-experience-section">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <Reveal>
          <Overline>Pick Your Vibe</Overline>
          <h2 className="mt-3 font-display text-5xl leading-none tracking-tight text-cream sm:text-7xl lg:text-8xl">
            FLAVOUR <span className="text-gold-gradient">EXPERIENCE</span>
          </h2>
        </Reveal>

        <div className="mt-14 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {FLAVOURS.map((f, i) => (
            <motion.div key={f.key}
              initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ delay: i * 0.08, duration: 0.6 }}
              onMouseEnter={() => setActive(f.key)} onMouseLeave={() => setActive(null)}
              className="group relative h-[420px] overflow-hidden rounded-3xl border border-white/10"
              data-testid={`flavour-card-${f.key}`}>
              <div className="absolute inset-0 transition-transform duration-700 group-hover:scale-105"
                style={{ background: `radial-gradient(circle at 50% 20%, ${f.color}55, #0A0A0A 70%)` }} />

              {/* floating ingredient particles on hover */}
              <AnimatePresence>
                {active === f.key && PARTICLES[f.key].map((p, idx) => (
                  <motion.span key={idx}
                    initial={{ opacity: 0, y: 40, scale: 0.5 }}
                    animate={{ opacity: [0, 1, 0], y: -120, scale: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 2.2, delay: idx * 0.3, repeat: Infinity }}
                    className="absolute text-3xl"
                    style={{ left: `${20 + idx * 28}%`, bottom: "30%" }}>
                    {p}
                  </motion.span>
                ))}
              </AnimatePresence>

              <img src={JAR[f.key]} alt={`${f.name} flavour`} loading="lazy"
                className="absolute left-1/2 top-8 w-40 -translate-x-1/2 rounded-2xl product-shadow transition-transform duration-700 group-hover:-translate-y-2 group-hover:scale-105" />

              <div className="absolute inset-x-0 bottom-0 p-6">
                <div className="text-4xl">{f.emoji}</div>
                <h3 className="mt-2 font-display text-4xl tracking-wide text-cream">{f.name}</h3>
                <p className="mt-1 text-sm italic text-white/60">{f.tagline}</p>
                <Link to={`/shop?flavour=${f.key}`} data-testid={`shop-flavour-${f.key}`}
                  className="mt-4 inline-flex items-center gap-1 font-oswald text-xs uppercase tracking-[0.15em]"
                  style={{ color: f.color }}>
                  Shop {f.name} <ArrowUpRight size={15} />
                </Link>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
